import {
  TerraUIMessageDataPartName,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { getToolConfig } from '../components/tool-config';

type MessagePart = NonNullable<TerraUIMessage['parts']>[number];

function stringField(record: unknown, key: string): string | undefined {
  if (!record || typeof record !== 'object') {
    return undefined;
  }

  const value: unknown = Reflect.get(record, key);

  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function toolCallIdOfPart(part: MessagePart): string | undefined {
  return part.type === TerraUIMessageDataPartName
    ? stringField(part.data, 'toolCallId')
    : undefined;
}

export function parentToolCallIdOfPart(part: MessagePart): string | undefined {
  if (part.type === TerraUIMessageDataPartName) {
    const data: unknown = part.data;
    const metadata: unknown =
      data && typeof data === 'object' ? Reflect.get(data, 'metadata') : null;

    return (
      stringField(metadata, 'parentToolCallId') ??
      toolCallIdOfPart(part)?.match(/^(.*):child:/)?.[1]
    );
  }

  if (part.type === 'text' || part.type === 'reasoning') {
    return stringField(part.providerMetadata?.metadata, 'parentToolCallId');
  }

  return undefined;
}

export function isSubagentToolPart(part: MessagePart): boolean {
  if (part.type !== TerraUIMessageDataPartName) {
    return false;
  }

  const toolName = stringField(part.data, 'toolName');

  return toolName ? getToolConfig(toolName).cardType === 'sub-agent' : false;
}

export function subagentOwnedPartIndexes({
  parts,
  parentToolCallId,
}: {
  parts: readonly MessagePart[];
  parentToolCallId: string;
}): number[] {
  const parentIndex = parts.findIndex(
    (part) => toolCallIdOfPart(part) === parentToolCallId,
  );

  if (parentIndex < 0) {
    return [];
  }

  const owned = new Set([parentToolCallId]);
  let unlabeledRunOpen = true;

  return parts.flatMap((part, index) => {
    if (index <= parentIndex) {
      return [];
    }

    const parent = parentToolCallIdOfPart(part);
    const belongs = parent
      ? owned.has(parent)
      : unlabeledRunOpen && part.type !== 'text' && !isSubagentToolPart(part);

    if (!belongs) {
      unlabeledRunOpen = false;

      return [];
    }

    const toolCallId = toolCallIdOfPart(part);

    if (toolCallId) {
      owned.add(toolCallId);
    }

    return [index];
  });
}
